import axios from "axios";
import { ApiError } from "../../Models/ApiError";
import { HandleError } from "../../Helpers/ErrorHandler";
import { PutAwayScanRequest } from "../../Models/PutAwayScanRequest";
import { PutAwayScanResponse } from "../../Models/PutAwayScanResponse";
import { PutAwayListResponse } from "../../Models/PutAwayListResponse";
import { BooleanResponse } from "../../Models/BooleanResponse";
import { TransHeaderResponse } from "../../Models/TransHeaderResponse";
import { ValidCountLocation } from "../../Models/ValidCountLocation";

const API_URL = process.env.REACT_APP_API_URL;

export const getValidCountLocationsAPI = async (serverUrl: string, dbKey: string): Promise<ValidCountLocation[]> => {
  return new Promise<ValidCountLocation[]>((resolve, reject) => {
    axios
      .get<ValidCountLocation[]>(`${API_URL}putaway/locations?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const getScanWarningCountAPI = async (serverUrl: string, dbKey: string): Promise<number | null> => {
  return new Promise<number | null>((resolve, reject) => {
    axios
      .get<number | null>(`${API_URL}putaway/scanwarningcount?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data ?? null);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const getPutAwayListAPI = async (serverUrl: string, dbKey: string): Promise<PutAwayListResponse> => {
  //we don't need to pass deviceId in querystring as it is passed in the header
  return new Promise<PutAwayListResponse>((resolve, reject) => {
    axios
      .get<PutAwayListResponse>(`${API_URL}putaway?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const scanPutAwayItemAPI = async (
  barcode: string,
  serverUrl: string,
  dbKey: string,
  includeRows: boolean = false,
  requireBin: boolean = false,
  commit: boolean = true,
  qty?: number,
  locationNo?: number,
): Promise<PutAwayScanResponse> => {
  const model: PutAwayScanRequest = {
    barcode,
    deviceId: undefined, //this is passed in the header, so don't need to set it here
  };
  let url = `${API_URL}putaway/scan?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`;
  if (includeRows) {
    url += `&includeRows=true`;
  }
  if (requireBin) {
    url += `&requireBin=true`;
  }
  if (!commit) {
    url += `&commit=false`;
  }
  if (qty !== undefined) {
    url += `&qty=${qty}`;
  }
  if (locationNo !== undefined && locationNo > 0) {
    url += `&locationNo=${locationNo}`;
  }
  return new Promise<PutAwayScanResponse>((resolve, reject) => {
    axios
      .post<PutAwayScanResponse>(url, model)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const updateItemQtyAPI = async (id: number, qty: number, serverUrl: string, dbKey: string): Promise<BooleanResponse> => {
  return new Promise<BooleanResponse>((resolve, reject) => {
    axios
      .put<BooleanResponse>(`${API_URL}putaway/item/qty?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}&id=${id}&qty=${qty}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const deletePutAwayItemAPI = async (stockNo: number, serverUrl: string, dbKey: string): Promise<BooleanResponse> => {
  return new Promise<BooleanResponse>((resolve, reject) => {
    axios
      .delete<BooleanResponse>(`${API_URL}putaway/item?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}&stockNo=${stockNo}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const deletePutAwayListAPI = async (serverUrl: string, dbKey: string): Promise<BooleanResponse> => {
  //deviceId is passed in the header, so don't need to pass it in queryString
  return new Promise<BooleanResponse>((resolve, reject) => {
    axios
      .delete<BooleanResponse>(`${API_URL}putaway/all?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const submitPutAwayListAPI = async (serverUrl: string, dbKey: string): Promise<TransHeaderResponse> => {
  return new Promise<TransHeaderResponse>((resolve, reject) => {
    axios
      .post<TransHeaderResponse>(`${API_URL}putaway/submit?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const submitStockCountAPI = async (
  serverUrl: string,
  dbKey: string,
  locationNo: number,
  batchDate: string,
  batchDescription: string,
): Promise<TransHeaderResponse> => {
  const model = {
    locationNo,
    batchDescription,
    batchDate,
  };
  return new Promise<TransHeaderResponse>((resolve, reject) => {
    axios
      .post<TransHeaderResponse>(`${API_URL}putaway/stockcount?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`, model)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};
